
import { CaughtError } from './events/CaughtError.event';
import { ResultTypeEnum, QosError } from './events/QosError.event';
import { Verbose, IVerboseSingleSchema } from './events/Verbose.event';
import { CircularBuffer } from './utilities/CircularBuffer';
import { safeSerialize } from '@ms/utilities-objects/lib/safeSerialize';

const MAX_VERBOSE_LOGS = 50;

export interface IQosErrorData {
    eventName: string | undefined;
    resultCode: string | undefined;
    resultType: ResultTypeEnum | undefined;
}

const CANCELED = 'Canceled';

const _verboseLogs: CircularBuffer<IVerboseSingleSchema> = new CircularBuffer<IVerboseSingleSchema>(MAX_VERBOSE_LOGS);

export function verbose(message: string, eventName?: string): void {
    _verboseLogs.push({
        name: eventName,
        message: message
    });
}

export function logError(
    error: Error | string | null | undefined | {
        message: string;
    } | {
        description: string;
    } | {},
    extraData?: { [key: string]: string | number | boolean | null | undefined; },
    qosData?: IQosErrorData
): void {
    if (!error || _isCanceled(error)) {
        return;
    }

    let message = getErrorMessage(error);

    let stack = error && typeof error === 'object' && ('stack' in error) && error.stack ? error.stack : '';

    let schema: IVerboseSingleSchema | null;
    while (schema = _verboseLogs.popOldest()) {
        Verbose.logData(schema);
    }

    if (qosData && qosData.eventName) {
        QosError.logData({
            name: qosData.eventName,
            resultCode: qosData.resultCode,
            resultType: qosData.resultType,
            extraData: extraData || {},
            message: message,
            stack: stack
        });
    } else {
        CaughtError.logData({
            extraData: extraData || {},
            message: message,
            stack: stack
        });
    }
}

export function log(
    error: Error | string | null | undefined | {
        message: string;
    } | {
        description: string;
    } | {},
    eventName?: string,
    resultCode?: string,
    resultType?: ResultTypeEnum
): void {
    logError(error, undefined, { eventName, resultCode, resultType });
}

/**
 * Extract the error message from the error
 */
export function getErrorMessage(
    error: Error | string | { toString(): string; } | { message: string; } | { description: string; } | {}
): string {
    let message = '';
    if (error) {
        if (typeof (error) === 'object' && 'message' in error) {
            message = error.message;
        } else if (typeof (error) === 'object' && 'description' in error) {
            message = error.description;
        } else if (typeof (error) === 'object') {
            message = safeSerialize(error);
        } else if (error.toString) {
            message = error.toString();
        }
    }
    return message;
}

function _isCanceled(error?: string | Error | { message: string; } | {}): boolean {
    return error === CANCELED ||
        (!!error && typeof error === 'object' && ('name' in error) && error.name === CANCELED) ||
        (!!error && typeof error === 'object' && ('message' in error) && error.message === CANCELED);
}
